import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { Check, Minus, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import Reveal from '@/components/motion/reveal'
import PixelText from '@/components/motion/pixel-text'
import PageHero from '@/components/generals/page-hero'
import FinalCta from '@/components/landing/final-cta'

interface Plan {
    name: string
    price: string
    cadence: string
    summary: string
    cta: string
    featured?: boolean
    lead: string
    features: string[]
}

const plans: Plan[] = [
    {
        name: 'Team',
        price: '$1,200',
        cadence: 'per month, billed yearly',
        summary: 'For a single product team that wants calmer on-call from week one.',
        cta: 'Start with Team',
        lead: 'Includes',
        features: [
            'Up to 50 services',
            'System Map and alert triage',
            '10 scheduled Watches',
            'Root cause with linked evidence',
            'Team chat integration',
            '30 days of investigation history'
        ]
    },
    {
        name: 'Scale',
        price: '$3,600',
        cadence: 'per month, billed yearly',
        summary: 'For engineering organizations running many services across several teams.',
        cta: 'Start with Scale',
        featured: true,
        lead: 'Everything in Team, plus',
        features: [
            'Up to 250 services',
            'Unlimited Watches',
            'Fixes drafted as pull requests',
            'Canary verification after release',
            'Decision memory across teams',
            'SSO and one year of history'
        ]
    },
    {
        name: 'Enterprise',
        price: 'Custom',
        cadence: 'annual agreement',
        summary: 'For companies with strict security, compliance or deployment needs.',
        cta: 'Talk to sales',
        lead: 'Everything in Scale, plus',
        features: [
            'Unlimited services',
            'EU or US data residency',
            'SCIM and audit log export',
            'Private deployment option',
            'A dedicated Forge engineer',
            '99.9% uptime commitment'
        ]
    }
]

type Cell = boolean | string

interface Row {
    label: string
    cells: [Cell, Cell, Cell]
}

const comparison: { group: string, rows: Row[] }[] = [
    {
        group: 'Understanding',
        rows: [
            { label: 'Services mapped', cells: ['50', '250', 'Unlimited'] },
            { label: 'Live System Map', cells: [true, true, true] },
            { label: 'Ownership from code owners files', cells: [true, true, true] },
            { label: 'Decision memory', cells: [false, true, true] }
        ]
    },
    {
        group: 'Triage and response',
        rows: [
            { label: 'Alert grouping and noise retirement', cells: [true, true, true] },
            { label: 'Root cause with linked evidence', cells: [true, true, true] },
            { label: 'Scheduled Watches', cells: ['10', 'Unlimited', 'Unlimited'] },
            { label: 'Fixes drafted as pull requests', cells: [false, true, true] },
            { label: 'Canary verification', cells: [false, true, true] }
        ]
    },
    {
        group: 'Security and support',
        rows: [
            { label: 'SOC 2 Type II', cells: [true, true, true] },
            { label: 'Single sign on', cells: [false, true, true] },
            { label: 'SCIM and audit log export', cells: [false, false, true] },
            { label: 'Data residency', cells: [false, false, true] },
            { label: 'Support', cells: ['Email', 'Priority', 'Dedicated engineer'] }
        ]
    }
]

const faqs = [
    {
        question: 'What counts as a service?',
        answer: 'Anything that deploys on its own: an API, a worker, a scheduled job or a database Forge maps as a separate node. Shared libraries and infrastructure modules are free.'
    },
    {
        question: 'Does Forge change anything without us?',
        answer: 'No. Forge starts with read-only access. Every change it proposes arrives as a pull request or a configuration diff, and nothing reaches production until someone on your team approves it.'
    },
    {
        question: 'How long does it take to see value?',
        answer: 'Most teams connect their tools in an afternoon. Forge builds its System Map within the first day and usually surfaces its first useful findings in the first week.'
    },
    {
        question: 'Can we try Forge before we commit?',
        answer: 'Yes. Every plan starts with a 30 day pilot on your own production, with a Forge engineer alongside you. If it does not earn its place, you owe nothing.'
    },
    {
        question: 'Where is our data stored?',
        answer: 'Team and Scale run in our US region. Enterprise customers can choose the EU or the US, or run Forge in a private deployment inside their own cloud account.'
    },
    {
        question: 'Can we pay monthly?',
        answer: 'Team and Scale are also available month to month at 15% above the yearly price. Enterprise agreements are annual.'
    }
]

const CellValue: FC<{ value: Cell }> = ({ value }): ReactNode => {
    if (value === true) {
        return (
            <>
                <Check className='mx-auto size-4 text-ink' strokeWidth={2} aria-hidden='true' />
                <span className='sr-only'>Included</span>
            </>
        )
    }

    if (value === false) {
        return (
            <>
                <Minus className='mx-auto size-4 text-ink/25' strokeWidth={2} aria-hidden='true' />
                <span className='sr-only'>Not included</span>
            </>
        )
    }

    return value
}

const PricingPage: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title='Pricing'
                description='Forge pricing for product teams, growing engineering organizations and enterprises. Every plan starts with a 30 day pilot on your own production.'
            />

            <PageHero
                eyebrow='Pricing'
                title='Priced by your systems, not your headcount.'
                description='Every plan includes the whole team. You pay for the services Forge looks after, and every plan starts with a 30 day pilot on your own production.'
            />

            <section aria-label='Plans' data-header-theme='light' className='border-b border-dashed border-line bg-paper'>
                <div className='container-page py-16 sm:py-24'>
                    <ul className='grid gap-3 lg:grid-cols-3'>
                        {plans.map((plan, index) => (
                            <Reveal as='li' key={plan.name} index={index} className='flex'>
                                <div
                                    className={cn(
                                        'flex w-full flex-col gap-8 rounded-2xl p-7 sm:p-8',
                                        plan.featured ? 'night-card text-night-ink' : 'card'
                                    )}
                                >
                                    <div className='flex flex-col gap-4'>
                                        <div className='flex items-center justify-between'>
                                            <PixelText
                                                as='h2'
                                                text={plan.name}
                                                className={cn('font-heading text-[1.9rem] font-normal leading-none tracking-[-0.02em]', plan.featured ? 'text-white' : 'text-ink')}
                                            />

                                            {plan.featured && (
                                                <span className='rounded-full bg-white/[0.08] px-3 py-1 text-[12px] text-night-ink'>
                                                    Most chosen
                                                </span>
                                            )}
                                        </div>

                                        <p className={cn('min-h-[3rem] text-pretty text-[15px] leading-relaxed', plan.featured ? 'text-night-muted' : 'text-ink-muted')}>
                                            {plan.summary}
                                        </p>
                                    </div>

                                    <div className='flex flex-col gap-1'>
                                        <span className={cn('font-heading text-[3rem] leading-none tracking-[-0.03em]', plan.featured ? 'text-white' : 'text-ink')}>
                                            {plan.price}
                                        </span>

                                        <span className={cn('text-[13px]', plan.featured ? 'text-night-subtle' : 'text-ink-subtle')}>
                                            {plan.cadence}
                                        </span>
                                    </div>

                                    <Link
                                        href='/demo'
                                        className={cn('btn btn-lg h-12 w-full text-[15px]', plan.featured ? 'btn-night' : 'btn-primary')}
                                    >
                                        {plan.cta}
                                    </Link>

                                    <div className={cn('flex flex-col gap-4 border-t border-dashed pt-7', plan.featured ? 'border-night-line' : 'border-line')}>
                                        <span className={cn('text-[13px]', plan.featured ? 'text-night-subtle' : 'text-ink-subtle')}>
                                            {plan.lead}
                                        </span>

                                        <ul className='flex flex-col gap-3'>
                                            {plan.features.map((feature) => (
                                                <li key={feature} className={cn('flex items-start gap-3 text-[15px]', plan.featured ? 'text-night-ink' : 'text-ink')}>
                                                    <Check className={cn('mt-[3px] size-4 flex-none', plan.featured ? 'text-improve' : 'text-healthy')} strokeWidth={2} aria-hidden='true' />
                                                    {feature}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </ul>
                </div>
            </section>

            <section aria-labelledby='compare-title' data-header-theme='light' className='border-b border-dashed border-line bg-paper-lifted'>
                <div className='container-page flex flex-col gap-10 py-24 sm:py-28'>
                    <PixelText
                        id='compare-title'
                        as='h2'
                        text='Compare plans in detail.'
                        className='font-heading text-[clamp(2rem,4vw,3rem)] font-normal leading-[1.06] tracking-[-0.025em] text-ink'
                    />

                    <Reveal variant='fade'>
                        <div className='card overflow-x-auto'>
                        <table className='w-full min-w-[40rem] border-collapse text-left text-[14.5px]'>
                            <thead>
                                <tr className='border-b border-line'>
                                    <th scope='col' className='w-[40%] px-6 py-5 font-normal text-ink-subtle'>
                                        <span className='sr-only'>Feature</span>
                                    </th>

                                    {plans.map((plan) => (
                                        <th key={plan.name} scope='col' className='px-4 py-5 text-center font-heading text-[1.25rem] font-normal text-ink'>
                                            {plan.name}
                                        </th>
                                    ))}
                                </tr>
                            </thead>

                            {comparison.map((section) => (
                                <tbody key={section.group}>
                                    <tr>
                                        <th colSpan={4} scope='colgroup' className='bg-ink/[0.025] px-6 py-3 font-mono text-[11px] font-normal uppercase tracking-[0.08em] text-ink-subtle'>
                                            {section.group}
                                        </th>
                                    </tr>

                                    {section.rows.map((row) => (
                                        <tr key={row.label} className='border-t border-line'>
                                            <th scope='row' className='px-6 py-4 font-normal text-ink'>
                                                {row.label}
                                            </th>

                                            {row.cells.map((cell, index) => (
                                                <td key={index} className='px-4 py-4 text-center text-ink-muted'>
                                                    <CellValue value={cell} />
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            ))}
                        </table>
                        </div>
                    </Reveal>
                </div>
            </section>

            <section aria-labelledby='faq-title' data-header-theme='light' className='bg-paper'>
                <div className='container-page grid gap-10 py-24 sm:py-32 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)] lg:gap-16'>
                    <div className='flex flex-col gap-5'>
                        <PixelText
                            id='faq-title'
                            as='h2'
                            text='Questions, answered.'
                            className='font-heading text-[clamp(2rem,4vw,3rem)] font-normal leading-[1.06] tracking-[-0.025em] text-ink'
                        />

                        <p className='max-w-[22rem] text-pretty text-[16px] leading-relaxed text-ink-muted'>
                            Something else on your mind?
                            {' '}
                            <Link href='/demo' className='text-ink underline decoration-ink/30 underline-offset-4 transition-colors duration-150 ease-out hover:decoration-ink'>
                                Talk to the team
                            </Link>
                            .
                        </p>
                    </div>

                    <ul className='flex flex-col border-t border-line'>
                        {faqs.map((faq) => (
                            <li key={faq.question} className='border-b border-line'>
                                <details className='group'>
                                    <summary className='flex cursor-pointer list-none items-center justify-between gap-6 py-6 text-[17px] text-ink [&::-webkit-details-marker]:hidden'>
                                        {faq.question}
                                        <Plus className='size-4 flex-none text-ink-muted transition-transform duration-200 ease-out group-open:rotate-45' aria-hidden='true' />
                                    </summary>

                                    <p className='max-w-[40rem] pb-6 text-pretty text-[15.5px] leading-relaxed text-ink-muted'>
                                        {faq.answer}
                                    </p>
                                </details>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            <FinalCta />
        </Layout>
    )
}

export default PricingPage
